/**
 * EMT sign-up page order (2026-10-09, item 9). Pure: every "where next" decision the sign-up pages
 * make lives here so scripts/qa/offline-experience-signup.mjs can check each account state.
 *
 * Flag `emt_onboarding_v2` OFF (today's order, unchanged):
 *   step1 Register -> step2 Credentials -> step3 Job preferences -> step4 Optional questions -> app
 * Flag ON:
 *   step1 Register -> step2 Credentials -> quick-questions ("Now or later?")
 *     Now   -> step3 Job preferences -> work-experience (method chooser) -> step4 Optional questions -> app
 *     Later -> app
 *   Every step after Register has Skip (moves to the next step) and Finish later (goes to the app).
 *
 * Register (step 1) creates a usable, signed-in account (onboarding.emt.completed = true), so no
 * later step is ever a gate: any page here can be left for the app at any point, and someone who
 * arrived from a job's Apply link goes straight back to that application after step 1.
 */

export const EMT_ONBOARDING_V2_FLAG = 'emt_onboarding_v2';
export const EMT_ONBOARDING_V2_FLAG_LABEL = 'EMT sign-up: quick questions and work history';
export const EMT_ONBOARDING_V2_FLAG_DESCRIPTION =
  'New EMT sign-up order: after credentials, ask "Answer a few quick questions now or later?" (job preferences, ' +
  'work history with a choice of short AI conversation, resume upload or typing it in, then optional questions). ' +
  'Off = the original four-step sign-up.';

/** Where every finished or abandoned sign-up lands. */
export const EMT_SIGNUP_EXIT_PATH = '/career-wallet?welcome=1';

export const EMT_SIGNUP_PATHS = {
  account: '/register/step1',
  certifications: '/register/step2',
  quickQuestions: '/register/quick-questions',
  preferences: '/register/step3',
  workExperience: '/register/work-experience',
  questions: '/register/step4',
} as const;

export type EmtSignupPage = keyof typeof EMT_SIGNUP_PATHS;

/** continue = saved and moving on, skip = moving on without saving, finishLater = leave for the app. */
export type EmtSignupAction = 'continue' | 'skip' | 'finishLater' | 'now' | 'later';

/**
 * Where to go after an action on a sign-up page.
 * `postLoginRedirect` is only consulted after the account step (it is the job-link flow:
 * /jobs/:id/apply, saved by PublicJobPage before sign-up); later steps never use it.
 */
export function resolveEmtSignupNext(input: {
  page: EmtSignupPage;
  action: EmtSignupAction;
  flagOn: boolean;
  postLoginRedirect?: string | null;
}): string {
  const { page, action, flagOn } = input;
  if (action === 'finishLater') return EMT_SIGNUP_EXIT_PATH;
  switch (page) {
    case 'account':
      return input.postLoginRedirect || EMT_SIGNUP_PATHS.certifications;
    case 'certifications':
      return flagOn ? EMT_SIGNUP_PATHS.quickQuestions : EMT_SIGNUP_PATHS.preferences;
    case 'quickQuestions':
      if (action === 'now' || action === 'continue') return EMT_SIGNUP_PATHS.preferences;
      return EMT_SIGNUP_EXIT_PATH; // later / skip
    case 'preferences':
      return flagOn ? EMT_SIGNUP_PATHS.workExperience : EMT_SIGNUP_PATHS.questions;
    case 'workExperience':
      return EMT_SIGNUP_PATHS.questions;
    case 'questions':
    default:
      return EMT_SIGNUP_EXIT_PATH;
  }
}

/**
 * Should a sign-up page (step 2 onward) send this visitor somewhere else before rendering?
 * Returns the path to go to, or null to stay.
 *  - auth / profile / flags still loading: stay (the page shows its loading state, never a guess)
 *  - signed out: Register
 *  - employer / institute: their own home (EMTRouteGuard does this too)
 *  - EMT whose account step is not done (or no profile yet): Register
 *  - a page that exists only in the new order, while the flag is OFF: the page that holds the same
 *    place in today's order (quick-questions -> step 3, work-experience -> step 4), so an old link or
 *    a flag switched off mid-sign-up never strands anyone
 * profileSteps (certifications / preferences / questions answered or skipped) never redirect:
 * every page can be revisited on a finished account.
 */
export function resolveEmtSignupPageRedirect(input: {
  page: Exclude<EmtSignupPage, 'account'>;
  authResolved: boolean;
  signedIn: boolean;
  role: string | null | undefined;
  accountDone: boolean;
  flagsLoaded: boolean;
  flagOn: boolean;
}): string | null {
  if (!input.authResolved) return null;
  if (!input.signedIn) return EMT_SIGNUP_PATHS.account;
  if (input.role === 'employer') return '/employer/dashboard';
  if (input.role === 'institute') return '/institute/dashboard';
  if (!input.accountDone) return EMT_SIGNUP_PATHS.account;
  const v2Only = input.page === 'quickQuestions' || input.page === 'workExperience';
  if (v2Only && input.flagsLoaded && !input.flagOn) {
    return input.page === 'quickQuestions' ? EMT_SIGNUP_PATHS.preferences : EMT_SIGNUP_PATHS.questions;
  }
  return null;
}

/**
 * "Step N of M" caption for a page. Register always says "Step 1 of 4" (it runs before sign-in, so
 * it cannot read the flag); in the new order the three quick questions together are step 4.
 * Flag off keeps today's captions exactly.
 */
export function emtSignupStepCaption(page: EmtSignupPage, flagOn: boolean): string {
  switch (page) {
    case 'account':
      return 'Step 1 of 4';
    case 'certifications':
      return 'Step 2 of 4';
    case 'quickQuestions':
      return 'Step 3 of 4';
    case 'preferences':
      return flagOn ? 'Step 4 of 4 · Quick questions 1 of 3' : 'Step 3 of 4';
    case 'workExperience':
      return 'Step 4 of 4 · Quick questions 2 of 3';
    case 'questions':
      return flagOn ? 'Step 4 of 4 · Quick questions 3 of 3' : 'Step 4 of 4';
    default:
      return '';
  }
}
